import _MinusIcon from "@adsk/alloy-react-icon/es/lib/MinusIcon";
import _CheckmarkIcon from "@adsk/alloy-react-icon/es/lib/CheckmarkIcon";
import _styled, { default as _styled2 } from "styled-components";
var _templateObject;
const _excluded = ["style", "className", "id", "name", "checked", "disabled", "defaultChecked", "onChange", "readOnly", "inputProps", "data-testid"],
  _excluded2 = ["style"];
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { useControlled } from '@adsk/alloy-react-helpers';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
const CONTROL_SIZE = 16;
const CONTROL_BORDER_WIDTH = 1;
const CLICK_TARGET_SIZE = 20;
const CLICK_TARGET_OFFSET = (CONTROL_SIZE - CLICK_TARGET_SIZE) / 2;
const INDETERMINATE = 'indeterminate';
const CheckboxStates = [true, false, INDETERMINATE];
const mapStateToAriaChecked = state => {
  if (state === INDETERMINATE) return 'mixed';
  return state;
};
const renderCheckmarkIcon = p => /*#__PURE__*/React.createElement(_CheckmarkIcon, p);
const renderMinusIcon = p => /*#__PURE__*/React.createElement(_MinusIcon, p);
const mapStateToIconRenderer = state => {
  if (state === true) return renderCheckmarkIcon;
  if (state === INDETERMINATE) return renderMinusIcon;
  return () => null;
};
const checkIndeterminateState = state => {
  // Expected behavior when using indeterminate checkbox state.
  if (state === INDETERMINATE) {
    return true;
  }
  return !state;
};
/**
 * A Checkbox
 * Provides a control to select from a list of non-exclusive options.
 * Checking the box should enable or select the active state.
 * (Don't phrase check boxes with a negative and require checking a box to turn something off.)
 * The rest of the props are propogated to the input element.
 */
const Checkbox = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      id,
      name,
      checked,
      disabled = false,
      defaultChecked = false,
      onChange: onChangeProp,
      readOnly,
      inputProps = {},
      'data-testid': dataTestid
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [value, handleOnChange] = useControlled(defaultChecked, checked, onChangeProp);
  const onChangeWrapper = e => handleOnChange(checkIndeterminateState(value), e);
  const onChange = readOnly ? undefined : e => {
    !disabled && onChangeWrapper(e);
  };
  const notUnchecked = value !== false;
  const {
      style: inputStyle
    } = inputProps,
    rest = _objectWithoutProperties(inputProps, _excluded2);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    className: className
  }, props, {
    $_css: [_objectSpread(_objectSpread(_objectSpread({
      display: 'block',
      height: CONTROL_SIZE,
      width: CONTROL_SIZE,
      borderWidth: notUnchecked ? 0 : CONTROL_BORDER_WIDTH,
      borderStyle: 'solid',
      borderRadius: theme.borderRadius.small,
      borderColor: theme.tokens.colors.border.value,
      backgroundColor: notUnchecked ? theme.tokens.colors.background.selected.value : 'inherit',
      cursor: 'pointer',
      color: theme.tokens.colors.icon.inverse.value
    }, getInteractiveStyles(disabled)), disabled && {
      backgroundColor: notUnchecked ? theme.tokens.colors.background.neutral.inactive.value : 'inherit',
      borderColor: theme.tokens.colors.border.inactive.value,
      color: theme.tokens.colors.icon.inactive.value
    }), {}, {
      '& svg': {
        display: 'block'
      },
      pointerEvents: disabled ? 'none' : 'auto'
    }), style]
  }), notUnchecked && mapStateToIconRenderer(value)({
    size: CONTROL_SIZE
  }), /*#__PURE__*/React.createElement(_StyledInput, _extends({
    ref: ref,
    id: id,
    name: name,
    value: value.toString(),
    type: "checkbox",
    onChange: onChange,
    readOnly: readOnly,
    checked: !!value,
    "aria-checked": mapStateToAriaChecked(value),
    disabled: disabled,
    "data-testid": dataTestid
  }, rest, {
    $_css2: [{
      // pointer-events needs to be 'none' when disabled for a wrapping
      // Tooltip to disappear correctly: https://github.com/react-component/tooltip/issues/18#issuecomment-411476678
      pointerEvents: disabled ? 'none' : 'auto',
      appearance: 'none',
      cursor: 'inherit',
      display: 'block',
      position: 'relative',
      opacity: 0,
      width: CLICK_TARGET_SIZE,
      height: CLICK_TARGET_SIZE,
      top: notUnchecked ? CLICK_TARGET_OFFSET - CONTROL_SIZE : CLICK_TARGET_OFFSET - CONTROL_BORDER_WIDTH,
      left: notUnchecked ? CLICK_TARGET_OFFSET : CLICK_TARGET_OFFSET - CONTROL_BORDER_WIDTH,
      margin: 0,
      padding: 0
    }, _objectSpread({}, inputStyle)]
  })));
});
Checkbox.displayName = 'Checkbox';
Checkbox.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the checkbox, used for the label */
  id: PropTypes.string,
  /** The name for the checkbox input */
  name: PropTypes.string,
  /** The value of the checkbox */
  checked: PropTypes.oneOf(CheckboxStates),
  /** The initial value of the checkbox, this will be read only if `checked` is undefined */
  defaultChecked: PropTypes.oneOf(CheckboxStates),
  /** onChange handler for the checkbox input */
  onChange: PropTypes.func,
  /** Disables propagation stop */
  readOnly: PropTypes.bool,
  /** Disable checkbox functionallity */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string
};
export default Object.assign(Checkbox, {
  INDETERMINATE
});
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledInput = _styled2("input").withConfig({
  displayName: "Checkbox___StyledInput",
  componentId: "sc-15wa5w9-0"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=Checkbox.js.map